import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@services/auth/auth.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { RrhhApiService } from '@services/rrhh/rrhh-api.service';
import { AuthFormComponent } from '@shared/components/auth-form/auth-form.component';
import { firstValueFrom } from 'rxjs';

/**
 * /acceso-portal/:token — the link of the «Acceso al portal» email. The employee sets a password,
 * the backend creates their CRM user and we sign them straight into their portal.
 */
@Component({
	selector: 'app-portal-access',
	imports: [AuthFormComponent, TranslocoModule],
	template: `
		<app-auth-form
			[isRegister]="3"
			[titleText]="name() ? ('auth.portalAccess.hello' | transloco: { name: name() }) : ('auth.portalAccess.title' | transloco)"
			[subtitleText]="email() ? ('auth.portalAccess.subtitle' | transloco: { email: email() }) : ''"
			[submitText]="'auth.portalAccess.submit' | transloco"
			[loading]="loading()"
			(formSubmit)="onSubmit($event)"
		></app-auth-form>
	`,
})
export class PortalAccessComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly api = inject(RrhhApiService);
	private readonly auth = inject(AuthService);
	private readonly alerts = inject(MessageAlertService);
	private readonly i18n = inject(TranslocoService);

	private readonly token = this.route.snapshot.paramMap.get('token') ?? '';
	readonly name = signal('');
	readonly email = signal('');
	readonly loading = signal(false);
	private valid = false;

	async ngOnInit(): Promise<void> {
		try {
			const info = await firstValueFrom(this.api.portalInviteInfo(this.token));
			this.valid = true;
			this.name.set(info.firstName);
			this.email.set(info.email);
		} catch (error) {
			this.reject(error);
		}
	}

	async onSubmit(form: { newPassword?: string }): Promise<void> {
		if (!this.valid || !form.newPassword || this.loading()) return;
		this.loading.set(true);
		try {
			const { email } = await firstValueFrom(this.api.acceptPortalInvite(this.token, form.newPassword));
			this.alerts.showMessage(
				'success',
				this.i18n.translate('auth.portalAccess.title'),
				this.i18n.translate('auth.portalAccess.created'),
			);
			// Signs in and redirects by role (monitor/employee → /portal, coordinator → /coordinacion).
			await firstValueFrom(this.auth.login(email, form.newPassword));
		} catch (error) {
			this.loading.set(false);
			this.alerts.showMessage('error', this.i18n.translate('auth.portalAccess.title'), this.message(error), 8000);
		}
	}

	private reject(error: unknown): void {
		this.alerts.showMessage('error', this.i18n.translate('auth.portalAccess.title'), this.message(error), 8000);
		setTimeout(() => void this.router.navigate(['/login']), 4000);
	}

	private message(error: unknown): string {
		const message = (error as { error?: { message?: string } })?.error?.message;
		return typeof message === 'string' && message.length < 300
			? message
			: this.i18n.translate('auth.portalAccess.genericError');
	}
}
